import KPI, { KPITypes } from 'models/kpi/kpi';
import TaskTypeFormElement, { TaskTypeFieldType } from 'models/tasks/taskTypeFormElement';
import { AuditCriterion, auditCriterionModeOneTime } from 'models/auditCriteria';
import { textToHtmlParagraphs } from 'utils/html';

// A DISPLAY projection of an entity's audit criteria onto form elements, so the criteria editor can
// render them through the very components a procedure form uses (TaskTypeFormEditor ->
// TaskTypeFormRenderer -> SingleFormElement) instead of a second set of cards that drifts from them.
//
// The REAL projection is the server's (AuditCriteriaFormProjector): it writes the fields onto every
// procedure form covering the entity, owns their ids and their order. Nothing built here is ever
// saved - the elements exist for the length of one modal, the commands on them write to the criteria
// SET through the criteria API, and the next load re-projects from the server's answer. So this only
// has to make a card look like the card the auditor will answer.
//
// The KPI ids are negative on purpose: they are not KPIs of this tenant, and the renderer only needs
// them to be unique (it keys each card on taskTypeId-fieldType-kpiId).
export const projectCriteriaToFormElements = (
  criteria: AuditCriterion[],
  entityType: number,
  entityId: number,
): TaskTypeFormElement[] => {
  return criteria.map((criterion, index) => {
    const kpi = new KPI();
    kpi.kpiId = -(index + 1);
    kpi.type = KPITypes.SuccesError;
    kpi.isField = true;
    //the requirement IS the question: the projector puts it in the value text and hides the label, or
    //every card would show the same sentence twice
    kpi.showLabel = false;
    kpi.name = criterion.requirement;
    kpi.defValue = textToHtmlParagraphs(criterion.requirement);
    kpi.expectedResult = criterion.successCriteria;
    kpi.instruction = getInstruction(criterion);
    kpi.required = criterion.required;
    //same encoding as the server: a positive comment mode demands one, a negative one caps its length
    kpi.commentMode = criterion.requiresComment ? 1 : -512;
    kpi.attachmentMode = criterion.requiresEvidence ? 1 : 0;

    const element = new TaskTypeFormElement();
    element.taskTypeId = 0;
    element.fieldType = TaskTypeFieldType.KPI;
    element.kpi = kpi;
    element.kpiId = kpi.kpiId;
    element.sortOrder = index;
    element.name = criterion.requirement;
    element.expectedResult = criterion.successCriteria;
    element.instruction = kpi.instruction;
    //what makes the card a CRITERION card: the question locks, the pencil opens the criterion editor
    //and the commands know which set to write to
    element.criterionKey = criterion.criterionKey;
    element.entityType = entityType;
    element.entityId = entityId;
    //a criterion that is not asked (not applicable or archived) renders as retired: read-only, never required
    element.retiredOn = criterion.isAsked() ? undefined : criterion.archivedOn ?? criterion.modified ?? criterion.created;
    element.criterionOneTime = criterion.mode === auditCriterionModeOneTime;
    element.criterionVerification = criterion.verification;
    element.criterionChangeKind = criterion.changeKind;
    element.criterionOneTimeState = criterion.oneTimeState;
    //the standards the criterion belongs to, as chips on the card: the editor is the ONE surface that
    //shows them - the server-projected procedure and execution forms carry none
    element.normIds = criterion.normIds ?? criterion.effectiveNormIds;

    return element;
  });
};

const getInstruction = (criterion: AuditCriterion): string | undefined => {
  const parts = [criterion.sourceText, criterion.conflictNote].filter((part) => !!part);

  return parts.length > 0 ? parts.join('\n\n') : undefined;
};
